import type { JourneyDetailDto } from "@/api/endpoints";
import { InfoTooltip } from "@/components/InfoTooltip";
import { fmtET } from "@/lib/format";
import { STAGE_LABEL, STAGE_TIP } from "../journeyLabels";
import { ratioLabel } from "../journeyView";

interface Props {
  journey: JourneyDetailDto;
}

const ROWS = ["intake", "preOnboarding", "awaitingRequest", "preRelease", "requestProcessing", "woSubmit", "woApproval"] as const;

/** Every stage with its start, end, business and calendar days, and the peer median; the Total row last. */
export function StageTimingTable({ journey }: Props) {
  const byKey = new Map(journey.stages.map((s) => [s.key, s]));
  const total = byKey.get("total");
  const peer = journey.peerGroup;
  const row = (key: (typeof ROWS)[number] | "total") => {
    const s = byKey.get(key);
    const started = s?.start != null;
    const ratio = ratioLabel(s?.bd ?? null, s?.peerMedianBd ?? null);
    return (
      <tr key={key} className={key === "total" ? "total" : s?.status === "current" ? "current" : undefined}>
        <td>
          {STAGE_LABEL[key]}
          <InfoTooltip inline text={STAGE_TIP[key]} />
          {key === "preRelease" && <span className="muted"> · folded into Request processing on the bar</span>}
        </td>
        <td className="muted">{started ? fmtET(s!.start) : "—"}</td>
        <td className="muted">{!started ? "—" : s!.status === "current" ? "in progress" : fmtET(s!.end)}</td>
        <td className="num">{started && s!.bd != null ? s!.bd.toFixed(2) : "—"}</td>
        <td className="num muted">{started && s!.cd != null ? s!.cd.toFixed(2) : "—"}</td>
        <td className="num">{s?.peerMedianBd != null ? s.peerMedianBd.toFixed(2) : "—"}</td>
        <td className={`num vs ${ratio.tone}`}>{started ? ratio.text : "—"}</td>
      </tr>
    );
  };
  return (
    <div className="section journey-timing">
      <h3>Stage timing compared with peers</h3>
      <table className="tbl">
        <thead>
          <tr><th>Stage</th><th>Start</th><th>End</th><th className="num">This, bd</th><th className="num">cd</th><th className="num">Peer median</th><th className="num">vs median</th></tr>
        </thead>
        <tbody>
          {ROWS.map(row)}
          {total && row("total")}
        </tbody>
      </table>
      <p className="peerline">
        {peer.sampleSize > 0
          ? `Peer group: ${peer.sampleSize} completed requests matched on ${peer.levelDescription ?? "client"}. Per-stage medians count the peers on which that stage ran to its end.`
          : "No peer group: fewer than five completed requests share this client, line of business and engagement type."}{" "}
        Business day = Mon–Fri on the Ontario calendar.
      </p>
    </div>
  );
}
